/**
 * 管理画面の接続設定。
 *
 * apps/web と**同じ `VITE_FIREBASE_*` 方式**(値も同じ paltyping-cb5ab を指す)。
 * 本体と違い、管理画面は Firebase 無しでは意味を成さないので、
 * 設定が欠けていれば起動時に「設定されていません」と出して止める。
 *
 * 必須:
 *   VITE_FIREBASE_API_KEY / VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID / VITE_FIREBASE_APP_ID
 * 任意:
 *   VITE_FIREBASE_EMULATOR=true          エミュレータへ接続(ローカル検証用)
 *   VITE_FIREBASE_EMULATOR_HOST          既定 127.0.0.1
 *   VITE_FIREBASE_AUTH_EMULATOR_PORT     既定 9099
 *   VITE_FIREBASE_FIRESTORE_EMULATOR_PORT 既定 8080
 *   VITE_CONTENT_BASE                    既定 /content/v1/
 *   VITE_ASSETS_BASE                     画像・音声の配信元(R2)。未設定なら非表示
 *
 * App Check は**当面初期化しない**。管理画面のドメインを reCAPTCHA Enterprise の
 * 承認済みドメインへ追加するまでは、有効にするとトークン取得に失敗して
 * かえって使えなくなるため(本体と同じ段階的ロールアウトの考え方)。
 */

export const DEFAULT_EMULATOR_HOST = '127.0.0.1';
export const DEFAULT_AUTH_EMULATOR_PORT = 9099;
export const DEFAULT_FIRESTORE_EMULATOR_PORT = 8080;
export const DEFAULT_CONTENT_BASE = '/content/v1/';

export interface EmulatorConfig {
  readonly host: string;
  readonly authPort: number;
  readonly firestorePort: number;
}

export interface AdminEnv {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
  /** null ならクラウドの本番プロジェクトに接続する。 */
  readonly emulator: EmulatorConfig | null;
}

export type EnvSource = Readonly<Record<string, string | undefined>>;

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

export function defaultEnvSource(): EnvSource {
  return (import.meta.env ?? {}) as EnvSource;
}

function trimmed(source: EnvSource, key: string): string | undefined {
  const value = source[key];
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text === '' ? undefined : text;
}

function port(source: EnvSource, key: string, fallback: number): number {
  const raw = trimmed(source, key);
  if (raw === undefined) return fallback;
  const value = Number.parseInt(raw, 10);
  return Number.isInteger(value) && value > 0 && value < 65536 ? value : fallback;
}

function isTrue(value: string | undefined): boolean {
  return value === 'true' || value === '1';
}

export function readEmulatorConfig(source: EnvSource = defaultEnvSource()): EmulatorConfig | null {
  if (!isTrue(trimmed(source, 'VITE_FIREBASE_EMULATOR'))) return null;
  return {
    host: trimmed(source, 'VITE_FIREBASE_EMULATOR_HOST') ?? DEFAULT_EMULATOR_HOST,
    authPort: port(source, 'VITE_FIREBASE_AUTH_EMULATOR_PORT', DEFAULT_AUTH_EMULATOR_PORT),
    firestorePort: port(
      source,
      'VITE_FIREBASE_FIRESTORE_EMULATOR_PORT',
      DEFAULT_FIRESTORE_EMULATOR_PORT,
    ),
  };
}

/** 必須キーが全て揃っていれば設定を返す。1つでも欠けたら null。 */
export function readAdminEnv(source: EnvSource = defaultEnvSource()): AdminEnv | null {
  const values = REQUIRED_KEYS.map((key) => trimmed(source, key));
  if (values.some((value) => value === undefined)) return null;
  const [apiKey, authDomain, projectId, appId] = values as [string, string, string, string];
  return { apiKey, authDomain, projectId, appId, emulator: readEmulatorConfig(source) };
}

/** 設定漏れを画面に出すための欠けキー一覧。 */
export function missingAdminEnvKeys(source: EnvSource = defaultEnvSource()): string[] {
  return REQUIRED_KEYS.filter((key) => trimmed(source, key) === undefined);
}

/** `content/v1/` の配信元。末尾スラッシュを正規化する。 */
export function readContentBase(source: EnvSource = defaultEnvSource()): string {
  const raw = trimmed(source, 'VITE_CONTENT_BASE') ?? DEFAULT_CONTENT_BASE;
  return raw.endsWith('/') ? raw : `${raw}/`;
}

/** 画像・音声の配信元(R2)。未設定なら null = アセット非表示。 */
export function readAssetsBase(source: EnvSource = defaultEnvSource()): string | null {
  const raw = trimmed(source, 'VITE_ASSETS_BASE');
  if (raw === undefined) return null;
  return raw.endsWith('/') ? raw : `${raw}/`;
}

/** コンテンツ側の相対パス(`img/w00014.webp`)を配信URLへ。未配備なら null。 */
export function assetUrl(relative: string | undefined, base: string | null): string | null {
  if (!relative || base === null) return null;
  return `${base}${relative.replace(/^\/+/, '')}`;
}
